/* jshint esversion: 6 */

/**
 * Service class to find various types of objects within a task context.
 * This class utilizes multiple services to fetch and filter objects based on their types and properties.
 */
class FindObjectService {
    /**
     * Constructs the FindObjectService instance.
     * @param {Object} TaskDataService - Service to fetch task-related data.
     * @param {Object} $config - Configuration service.
     * @param {Object} CMSMediaInfoService - Service to fetch media-related information.
     * @param {Object} ObjectInstanceRegistrationService - Service to register and fetch object instances.
     */
    constructor(
        TaskDataService,
        $config,
        CMSMediaInfoService,
        ObjectInstanceRegistrationService
    ) {
        // 'ngInject'; // Remove this line if present
        this.TaskDataService = TaskDataService;
        this.$config = $config;
        this.CMSMediaInfoService = CMSMediaInfoService;
        this.ObjectInstanceRegistrationService = ObjectInstanceRegistrationService;

        this._taskContext = TaskDataService.fetchContext();
        this._availableAdvancedObjectTypes = $config.getAdvancedObjectTypesList();
        this._availableSpecialContainerTypes = $config.getSpecialObjectContainerList();
        this._availableMediaTypes = CMSMediaInfoService.getAvailableMediaTypes();
    }

    /**
     * Retrieves all content objects from the task context.
     * @returns {Array} List of content objects.
     */
    getContentObjects() {
        return this._taskContext.findInstancesByType('contentObject');
    }

    /**
     * Retrieves all container objects from the task context.
     * @returns {Array} List of container objects.
     */
    getContainerObjects() {
        return this._taskContext.findInstancesByType('container');
    }

    /**
     * Retrieves a list of available advanced object types from the content objects.
     * @returns {Array} List of available advanced object types.
     */
    getAvailableAdvancedObjectTypes() {
        const _contentObjects = this.getContentObjects(),
            _advancedObjectList = this.$config.getAdvancedObjectTypesList(),
            list = [];

        _contentObjects.forEach(obj => {
            if ('getObjectType' in obj &&
                _advancedObjectList.includes(obj.getObjectType())) {

                const otype = obj.getObjectType();
                if (!list.includes(otype)) {
                    list.push(otype);
                }
            }
        });
        return list;
    }

    /**
     * Retrieves advanced objects by a specific type.
     * @param {string} advType - The type of advanced object to retrieve.
     * @returns {Object|boolean} Object containing advanced objects of the specified type or false if the type is invalid.
     */
    getAdvancedObjectsByType(advType) {
        const _objectList = this.getContentObjects(),
            _advancedObjectList = this.getAvailableAdvancedObjectTypes(),
            _instances = {};

        if (!_advancedObjectList.includes(advType)) {
            console.error('"%s" is not a valid advanced object type. Available types for this Assessment are: %s.', advType, _advancedObjectList.join(','));
            return false;
        }

        _objectList.forEach(obj => {
            if ('getObjectType' in obj && advType === obj.getObjectType()) {
                _instances[obj.uuid] = obj;
            }
        });
        return _instances;
    }

    /**
     * Retrieves all advanced objects from the available advanced object types.
     * @returns {Object} Object containing all advanced objects.
     */
    getAdvancedObjects() {
        const _availableAdvancedObject = this.getAvailableAdvancedObjectTypes(),
            _instances = {};

        _availableAdvancedObject.forEach(type => {
            const objs = this.getAdvancedObjectsByType(type);
            Object.assign(_instances, objs);
        });
        return _instances;
    }

    /**
     * Retrieves all iframe tool objects from the task context.
     * @returns {Object} Object containing iframe tool objects.
     */
    getIframeToolsObjects() {
        const objs = this._taskContext.findInstancesByType('iframeTool'),
            result = {};

        objs.forEach(obj => {
            result[obj.uuid] = {
                'description': obj.getDescription(),
                'icon': obj.getIcon() ? obj.getIcon().getUUID() : '',
                'media': obj.getIframeTool()
            };
        });
        return result;
    }

    /**
     * Retrieves content objects by a specific content type.
     * @param {string} contentType - The type of content object to retrieve.
     * @returns {Object} Object containing content objects of the specified type.
     */
    getContentObjectsByObjectType(contentType) {
        const objs = this.getContentObjects();
        const _instances = {};

        for (const uuid in objs) {
            if (!objs[uuid].hasOwnProperty('getObjectType')) {
                continue;
            }
            if (contentType === objs[uuid].getObjectType()) {
                _instances[uuid] = {
                    'content': objs[uuid].getContent() || '',
                    'contentReference': objs[uuid].getContentReference() || false,
                    'preferences': objs[uuid].getPreferences() || {},
                    'entity_content': 'data' in objs[uuid] && objs[uuid].data ? objs[uuid].data['entity_content'] : false
                };
            }
        }
        return _instances;
    }

    /**
     * Retrieves all subtitle objects from the task context.
     * @returns {Object} Object containing subtitle objects.
     */
    getSubtitleObjects() {
        const objs = this._taskContext.findInstancesByType('media');
        const result = {};

        for (const uuid in objs) {
            if (objs[uuid].hasOwnProperty('getUploadName')) {
                const name = objs[uuid].getUploadName();
                const ext = name.substring(name.lastIndexOf(".") + 1).toLowerCase();
                if (ext === 'srt') {
                    result[uuid] = {
                        'uploadName': name,
                        'mimeType': objs[uuid].getMimeType()
                    };
                }
            }
        }
        return result;
    }

        /**
     * Retrieves all image objects from the task context.
     * @returns {Object} Object containing image objects.
     */
        getImageObjects() {
            const objs = this._taskContext.findInstancesByType('media');
            const result = {};
    
            for (const uuid in objs) {
                if (objs[uuid].hasOwnProperty('getUploadName')) {
                    const name = objs[uuid].getUploadName();
                    const ext = name.substring(name.lastIndexOf(".") + 1).toLowerCase();
                    if (['jpg', 'png', 'gif', 'svg'].includes(ext)) {
                        result[uuid] = {
                            'uploadName': name,
                            'mimeType': objs[uuid].getMimeType()
                        };
                    }
                }
            }
            return result;
        }    

    /**
     * Retrieves hint objects within a specified container.
     * @param {Object} parentContainer - The parent container object.
     * @returns {Object} Object containing hint objects within the specified container.
     */
    getHintObjectsInContainer(parentContainer) {
        const containerObjectsUUID = parentContainer.data.objects,
            hintObjects = this.ObjectInstanceRegistrationService.getNamedObjectsByType('hint'),
            hintObjectsInContainer = {},
            objectNames = this.ObjectInstanceRegistrationService.getObjectNames();

        containerObjectsUUID.forEach(key => {
            if (hintObjects[key]) {
                hintObjectsInContainer[key] = objectNames[key];
            }
        });
        return hintObjectsInContainer;
    }

    /**
     * Locates media in the overview section
     */
    locateMediaInOverview(mediaUUID) {
        return this.getMediaReferencesInOverview()[mediaUUID] || false;
    }

    /**
     * Locates media in palettes
     */
    locateMediaInPalettes(mediaUUID) {
        return this.getMediaReferencesInUniversalCanvasPalettes()[mediaUUID] || false;
    }

    /**
     * Locates media used as iframe tool icon
     */
    locateMediaAsIconOfIframeTool(mediaUUID) {
        return this.getMediaReferencesAsIconInIframeTools()[mediaUUID] || false;
    }

    /**
     * Locates media used as video subtitle
     */
    locateMediaAsVideoSubtitle(mediaUUID) {
        return this.getMediaReferencesAsVideoSubtitle()[mediaUUID] || false;
    }

    /**
     * Locates media used as audio background
     */
    locateMediaAsAudioBackground(mediaUUID) {
        return this.getMediaReferencesAsAudioBackground()[mediaUUID] || false;
    }

    /**
     * Locates media in the task
     */
    locateMedia(mediaUUID) {
        const mediaContainers = this.CMSMediaInfoService.getMediaContainers();
        const result = [];

        if (mediaContainers[mediaUUID]) {
            for (const idx in mediaContainers[mediaUUID]) {
                const layout = this.getContainerLayout(mediaContainers[mediaUUID][idx]);

                if (layout) {
                    const sheet = this.getLayoutSheet(layout.getUUID());

                    // Check if it's in a question part
                    const questionPart = this.getSheetQuestionPartName(sheet.sheetUUID, mediaUUID);

                    if (questionPart) {
                        sheet.shortTitle = sheet.shortTitle + questionPart;
                    }

                    if (sheet) {
                        result.push(sheet);
                    }
                }
            }
        }

        return result.length ? result : false;
    }

    /**
     * Gets container layout
     * @param {string} containerUUID - Container's UUID
     * @returns {Object} Layout Object
     */
    getContainerLayout(containerUUID) {
        const _layouts = this._taskContext.findInstancesByType('layout');
        let result;

        layout:
        for (const idx in _layouts) {
            const layout_rows = _layouts[idx].getRows();

            for (const rows_idx in layout_rows) {
                const cols = layout_rows[rows_idx].cols;
                
                for (const cols_idx in cols) {
                    const col_list = cols[cols_idx].list;

                    for (const list_idx in col_list) {
                        if (col_list[list_idx].content.indexOf(containerUUID) !== -1) {
                            result = _layouts[idx];
                            break layout;
                        }
                    }
                }
            }
        }
        return result;
    }

    /**
     * Gets layout sheet
     * @param {string} layoutUUID - Layout's UUID
     * @returns {Object} Sheet info object
     */
    getLayoutSheet(layoutUUID) {
        const _sheets = this._taskContext.findInstancesByType('sheet');
        let result;

        sheet:
        for (const sheet_idx in _sheets) {
            if (layoutUUID === _sheets[sheet_idx].getLayout().getUUID()) {
                const shortTitle = _sheets[sheet_idx].isJournal() ? 
                    (_sheets[sheet_idx].getTitle() ? _sheets[sheet_idx].getTitle() + ' (Journal)' : 'Journal') : 
                    (_sheets[sheet_idx].isPreRelease() ? 
                        (_sheets[sheet_idx].getTitle() ? _sheets[sheet_idx].getTitle() + ' (Pre Release)' : 'Pre Release') : 
                        _sheets[sheet_idx].getShortTitle());
                
                result = {
                    'shortTitle': shortTitle,
                    'title': _sheets[sheet_idx].getTitle(),
                    'useCustomTitle': _sheets[sheet_idx].isUseCustomTitle(),
                    'sheetUUID': sheet_idx
                };
                break sheet;
            }
        }
        return result;
    }

    /**
     * Gets sheet question part name
     */
    getSheetQuestionPartName(sheetUUID, mediaUUID) {
        const mediaContainers = this.CMSMediaInfoService.getMediaContainers()[mediaUUID];
        const questionParts = this._taskContext.read(sheetUUID).getQuestionParts();
        const questionPartsUUIDs = this.getSheetQuestionPartsUUIDs(questionParts);

        if (questionParts.length === 1 && questionParts[0].data.isDefaultQuestionPart) {
            return '';
        } else if (questionParts.length === 1 && !questionParts[0].data.isDefaultQuestionPart) {
            return questionParts[0].getShortTitle();
        } else {
            for (const i in mediaContainers) {
                const container = this._taskContext.read(mediaContainers[i]);
                if ('getQuestionPart' in container && container.getQuestionPart()) {
                    const questionPartUUID = container.getQuestionPart().getUUID();
                    if (questionPartsUUIDs.indexOf(questionPartUUID) !== -1) {
                        return this._taskContext.read(questionPartUUID).getShortTitle();
                    }
                }
            }
        }
        return '';
    }

    /**
     * Gets media references in overview
     */
    getMediaReferencesInOverview() {
        const _sheets = this._taskContext.findInstancesByType('sheet');
        const result = {};

        for (const index in _sheets) {
            if ('getPreviewImages' in _sheets[index]) {
                const tmp = _sheets[index].getPreviewImages();
                
                if (tmp && tmp.length) {
                    for (const i in tmp) {
                        if ('getContentReference' in tmp[i] && 'getObjectType' in tmp[i]) {
                            const ref = tmp[i].getContentReference();
                            const type = tmp[i].getObjectType();

                            if (this._availableMediaTypes.indexOf(type) !== -1 && ref) {
                                if ('getUUID' in ref) {
                                    const uuid = ref.getUUID();

                                    if (uuid) {
                                        if (!(result[uuid] instanceof Array)) {
                                            result[uuid] = [];
                                        }

                                        if (!this.findInArray(result[uuid], 'sheetUUID', index)) {
                                            result[uuid].push({
                                                'shortTitle': _sheets[index].getShortTitle(),
                                                'title': _sheets[index].getTitle(),
                                                'useCustomTitle': _sheets[index].isUseCustomTitle(),
                                                'sheetUUID': index
                                            });
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return result;
    }

    /**
     * Gets media references in universal canvas palettes
     */
    getMediaReferencesInUniversalCanvasPalettes() {
        const _palettes = this._taskContext.findInstancesByType('universalCanvasPalette');
        const result = {};

        for (const uuid in _palettes) {
            for (const item in _palettes[uuid].getItems()) {
                const media = _palettes[uuid].getItems()[item];

                if (media) {
                    if (!(result[media.uuid] instanceof Array)) {
                        result[media.uuid] = [];
                    }

                    if (!this.findInArray(result[media.uuid], 'uuid', uuid)) {
                        result[media.uuid].push({
                            'uuid': uuid,
                            'name': _palettes[uuid].getName()
                        });
                    }
                }
            }
        }
        return result;
    }

    /**
     * Gets media references as icons in iframe tools
     */
    getMediaReferencesAsIconInIframeTools() {
        const iframeObjs = this._taskContext.findInstancesByType('iframeTool');
        const result = {};

        for (const uuid in iframeObjs) {
            if ('getIcon' in iframeObjs[uuid] && iframeObjs[uuid].getIcon() !== undefined) {
                const media = iframeObjs[uuid].getIcon();

                if (media) {
                    const mediaUUID = media.getUUID() || false;
                    if (mediaUUID && !(result[mediaUUID] instanceof Array)) {
                        result[mediaUUID] = [];
                    }

                    if (mediaUUID && !this.findInArray(result[mediaUUID], 'uuid', uuid)) {
                        result[mediaUUID].push({
                            'uuid': uuid,
                            'name': iframeObjs[uuid].getDescription() || undefined
                        });
                    }
                }
            }
        }
        return result;
    }

    /**
     * Gets media references as video subtitle
     */
    getMediaReferencesAsVideoSubtitle() {
        const videoObjs = this.getContentObjectsByObjectType('video');
        const subtitleObjs = this.getSubtitleObjects();
        const result = {};

        for (const uuid in videoObjs) {
            if (videoObjs[uuid].content && this.isUUID(videoObjs[uuid].content)) {
                const strUUID = videoObjs[uuid].content;
                if (subtitleObjs[strUUID]) {
                    if (!(result[strUUID] instanceof Array)) {
                        result[strUUID] = [];
                    }

                    if (!this.findInArray(result[strUUID], 'videoUUID', uuid)) {
                        if ('contentReference' in videoObjs[uuid] && videoObjs[uuid].contentReference && 'getUploadName' in videoObjs[uuid].contentReference) {
                            result[strUUID].push({
                                'videoName': videoObjs[uuid].contentReference.getUploadName() || false,
                                'videoUUID': videoObjs[uuid].contentReference.getUUID() || false,
                                'contentObjectUUID': uuid,
                                'question': this.locateObjectByContentObjectUUID(uuid)
                            });
                        } else {
                            result[strUUID].push({
                                'videoName': false,
                                'videoUUID': false,
                                'contentObjectUUID': uuid,
                                'question': this.locateObjectByContentObjectUUID(uuid)
                            });
                        }
                    }
                }
            }
        }
        return result;
    }

    /**
     * Gets media references as audio background
     */
    getMediaReferencesAsAudioBackground() {
        const audioObjs = this.getContentObjectsByObjectType('audio');
        const imageObjs = this.getImageObjects();
        const result = {};

        for (const uuid in audioObjs) {
            if (audioObjs[uuid].entity_content !== undefined && this.isUUID(audioObjs[uuid].entity_content.attachement)) {
                const strUUID = audioObjs[uuid].entity_content.attachement;
                if (imageObjs[strUUID]) {
                    if (!(result[strUUID] instanceof Array)) {
                        result[strUUID] = [];
                    }

                    if (!this.findInArray(result[strUUID], 'audioUUID', uuid)) {
                        result[strUUID].push({
                            'audioName': audioObjs[uuid].contentReference.getUploadName(),
                            'audioUUID': audioObjs[uuid].contentReference.getUUID(),
                            'contentObjectUUID': uuid,
                            'question': this.locateObjectByContentObjectUUID(uuid)
                        });
                    }
                }
            }
        }
        return result;
    }

    /**
     * Helper method to find an item in an array by key/value
     */
    findInArray(arr, key, value) {
        for (const i in arr) {
            if (arr[i][key] === value) {
                return true;
            }
        }
        return false;
    }

    /**
     * Helper method to check if a string is a UUID
     */
    isUUID(str) {
        const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        return pattern.test(str);
    }

    /**
     * Helper method to get sheet question parts UUIDs
     */
    getSheetQuestionPartsUUIDs(questionParts) {
        return Object.keys(questionParts).map(q => questionParts[q].getUUID());
    }

    /**
     * Gets content object containers
     * @returns {Object} - Map of content objects to their containers
     */
    getContentObjectContainers() {
        const _containers = this.getContainerObjects();
        const result = {};

        for (const uuid in _containers) {
            if (!_containers[uuid]) continue;

            const objects = _containers[uuid].getObjects();
            const containerUUID = _containers[uuid].getUUID();

            if (objects && objects.length) {
                for (let i = 0; i < objects.length; i++) {
                    if (!(result[objects[i]] instanceof Array)) {
                        result[objects[i]] = {};
                    }
                    result[objects[i]] = { 'container': containerUUID };
                }
            }
        }
        return result;
    }

    /**
     * Locates object by content object UUID
     * @param {string} contentObjectUUID - UUID of the content object
     * @returns {Object|boolean} - Sheet info or false if not found
     */
    locateObjectByContentObjectUUID(contentObjectUUID) {
        const objects = this.getContentObjectContainers()[contentObjectUUID] || false;

        if (!objects) {
            return false;
        }

        const containerLayout = this.getContainerLayout(objects.container);
        let layoutSheet;

        if (containerLayout) {
            layoutSheet = this.getLayoutSheet(containerLayout.getUUID());
        }

        return layoutSheet || false;
    }

    /**
     * Gets media referenced in iframe tools
     * @returns {Object} - Collection of media references in iframe tools
     */
    getMediaReferencedInIframeTool() {
        const iframeObjs = this._taskContext.findInstancesByType('iframeTool');
        const result = {};

        for (const uuid in iframeObjs) {
            if ('getIframeTool' in iframeObjs[uuid] && iframeObjs[uuid].getIframeTool()) {
                const iframeToolUUID = iframeObjs[uuid].getIframeTool().getUUID() || false;
                if (iframeToolUUID) {
                    if (!(result[iframeToolUUID] instanceof Array)) {
                        result[iframeToolUUID] = [];
                    }

                    if (!this.findInArray(result[iframeToolUUID], 'uuid', uuid)) {
                        result[iframeToolUUID].push({
                            'uuid': uuid,
                            'description': iframeObjs[uuid].getDescription() || undefined
                        });
                    }
                }
            }
        }
        return result;
    }
}

// Add explicit dependency injection
FindObjectService.$inject = [
    'TaskDataService',
    '$config',
    'CMSMediaInfoService',
    'ObjectInstanceRegistrationService'
];

export default FindObjectService;